/**
 * The shared toon material.
 *
 * ONE gradient ramp for the entire game. This is the single most important
 * decision for visual consistency: assets generated by Meshy, Tripo and Rodin on
 * three different days, in three different styles, all get flattened into the
 * same 3-band cel shading and start to look like one game.
 *
 * Implementation note: this uses Three's built-in MeshToonMaterial rather than a
 * custom shader. MeshToonMaterial already does exactly the banded NdotL lookup we
 * want, works on WebGL2 everywhere including mobile Safari, and costs us zero
 * GLSL to maintain. Do not replace it with a hand-written ShaderMaterial without
 * a concrete reason - see ARCHITECTURE.md on why we are not on WebGPU/TSL.
 */

import * as THREE from 'three';
import { tuning } from '@/core/tuning';

let gradientMap: THREE.DataTexture | null = null;

/**
 * The shared 3-band ramp. Built once, reused by every material in the game.
 * NearestFilter is what produces hard band edges instead of a gradient.
 */
export function getToonGradientMap(): THREE.DataTexture {
  if (gradientMap) return gradientMap;

  // Deliberately not linearly spaced: a wide bright band and a narrow, fairly
  // light shadow band keeps the art cheerful. A dark bottom band would read as
  // moody, which is wrong for this game. Tune in data/tuning.json (render.toonBands);
  // three bands reads as "cartoon", more starts to look soft.
  const stops = tuning.render.toonBands;
  const bands = Math.max(2, stops.length);
  const data = new Uint8Array(bands);
  for (let i = 0; i < bands; i++) data[i] = Math.round((stops[i] ?? 1) * 255);

  gradientMap = new THREE.DataTexture(data, bands, 1, THREE.RedFormat);
  gradientMap.minFilter = THREE.NearestFilter;
  gradientMap.magFilter = THREE.NearestFilter;
  gradientMap.generateMipmaps = false;
  gradientMap.needsUpdate = true;
  return gradientMap;
}

/** Cache keyed by color, so a level of 80 props shares a handful of materials. */
const materialCache = new Map<string, THREE.MeshToonMaterial>();
/** Includes per-model materials as well as the shared placeholder palette. */
const toonMaterials = new Set<THREE.MeshToonMaterial>();

export interface ToonMaterialOptions {
  transparent?: boolean;
  opacity?: number;
  /** Skip the cache. Use when the caller intends to mutate the material. */
  unique?: boolean;
}

/**
 * A cel-shaded material for the given color.
 *
 * Cached by default. Material count is a hard budget (see tuning.budgets), and
 * sharing materials is what lets the renderer batch draw calls on mobile.
 */
export function makeToonMaterial(
  color: string,
  options: ToonMaterialOptions = {},
): THREE.MeshToonMaterial {
  const transparent = options.transparent ?? false;
  const opacity = options.opacity ?? 1;
  const key = `${color}|${transparent}|${opacity}`;

  if (!options.unique) {
    const cached = materialCache.get(key);
    if (cached) return cached;
  }

  const material = new THREE.MeshToonMaterial({
    color: new THREE.Color(color),
    gradientMap: getToonGradientMap(),
    transparent,
    opacity,
  });
  registerToonMaterial(material);

  if (!options.unique) materialCache.set(key, material);
  return material;
}

/**
 * Applies the shared ramp to every material in an imported GLB subtree,
 * converting whatever the AI tool produced into our look.
 *
 * This runs at load time on real models. It is the runtime half of the
 * consistency guarantee - the importer script handles geometry and textures,
 * this handles shading.
 */
export function applyToonShading(root: THREE.Object3D): void {
  // GLBs commonly reuse one material across many mesh primitives. Preserve that
  // sharing or a one-material cat becomes dozens of material instances at load.
  const converted = new Map<THREE.Material, THREE.MeshToonMaterial>();
  root.traverse((node) => {
    if (!(node instanceof THREE.Mesh)) return;
    const source = node.material;
    const materials = Array.isArray(source) ? source : [source];

    node.material = materials.map((mat) => {
      const existing = converted.get(mat);
      if (existing) return existing;

      const toon = new THREE.MeshToonMaterial({ gradientMap: getToonGradientMap() });
      // Carry over whatever the source had; drop everything PBR.
      if ('color' in mat && mat.color instanceof THREE.Color) toon.color.copy(mat.color);
      if ('map' in mat && mat.map instanceof THREE.Texture) toon.map = mat.map;
      toon.transparent = mat.transparent;
      toon.opacity = mat.opacity;
      toon.alphaTest = mat.alphaTest;
      toon.side = mat.side;
      toon.name = `${mat.name || 'imported'}:toon`;
      registerToonMaterial(toon);
      converted.set(mat, toon);
      return toon;
    });
    if (Array.isArray(node.material) && node.material.length === 1) {
      node.material = node.material[0] as THREE.Material;
    }

    node.castShadow = true;
    node.receiveShadow = true;
  });
}

/* ------------------------------------------------------------------ */
/* Desaturation, for Cat Instinct                                      */
/* ------------------------------------------------------------------ */

let desaturation = 0;
/** Full-colour originals, captured lazily so a restore is always exact. */
const baseColors = new WeakMap<THREE.MeshToonMaterial, THREE.Color>();

/**
 * Drains colour from the whole shared palette. 0 is normal, 1 is greyscale.
 *
 * Deliberately NOT a post-processing pass: a full-screen effect would cost a
 * second render target on exactly the mobile hardware this game is budgeted for,
 * and would pull in a dependency the project does not have. Mutating the handful
 * of shared toon materials once per toggle is O(material families), not
 * O(objects), and touches no per-object state at all.
 *
 * Imported model materials are registered by `applyToonShading`, so the hero
 * characters and the shared placeholder palette always change together.
 */
export function setSharedDesaturation(amount: number): void {
  const clamped = Math.min(1, Math.max(0, amount));
  if (clamped === desaturation) return;
  desaturation = clamped;

  for (const material of toonMaterials) applyDesaturation(material, clamped);
}

export function currentDesaturation(): number {
  return desaturation;
}

/** Number of distinct cached materials. Checked against the material budget. */
export function materialFamilyCount(): number {
  return materialCache.size;
}

export function disposeMaterialCache(): void {
  for (const material of materialCache.values()) {
    toonMaterials.delete(material);
    material.dispose();
  }
  materialCache.clear();
}

function registerToonMaterial(material: THREE.MeshToonMaterial): void {
  if (toonMaterials.has(material)) return;
  toonMaterials.add(material);
  baseColors.set(material, material.color.clone());
  applyDesaturation(material, desaturation);
}

function applyDesaturation(material: THREE.MeshToonMaterial, amount: number): void {
  const base = baseColors.get(material);
  if (!base) return;
  // Rec. 709 luminance, so a bright yellow greys out lighter than a deep blue
  // rather than every colour collapsing to the same mid-grey.
  const grey = base.r * 0.2126 + base.g * 0.7152 + base.b * 0.0722;
  material.color.setRGB(
    base.r + (grey - base.r) * amount,
    base.g + (grey - base.g) * amount,
    base.b + (grey - base.b) * amount,
  );
}
